import { userHasPermissions } from '@ab-core-functions/user-permission';
import { ROUTES, logIn, useIsFeatureVisible, userIsLocked } from '@ab-core/functions';
import { useAuth } from '@ab-core/hooks';
import { navigate } from 'gatsby';
import type React from 'react';
import type { FC } from 'react';

type ProtectedRouteProps = {
    children: React.ReactElement | null;
    redirectAfterLogin?: boolean;
    featureName?: string;
    requiredPermission?: string;
    permissionBypassQuery?: {
        name: string;
        value: string;
    };
    locationSearch?: string;
};

export const ProtectedRoute: FC<ProtectedRouteProps> = (props) => {
    const {
        children,
        redirectAfterLogin,
        featureName = '',
        requiredPermission = '',
        permissionBypassQuery,
        locationSearch = ''
    } = props;
    const { user, userLoading } = useAuth();
    const { isFeatureVisible } = useIsFeatureVisible();

    // Ausnahme für Seiten, die dieselbe Vorlage aus mehreren Quellen bedienen: die
    // Barcodeliste ist per seeShoppinglists geschützt, wird mit ?type=cart aber aus dem
    // Warenkorb aufgerufen, der selbst keine Permission verlangt. Bewusst eng halten und
    // nur für solche Fälle in protectedSites.json setzen - der Query-Parameter hebt die
    // Permission-Prüfung vollständig auf (Login und serverseitige Scopes greifen weiter).
    const queryBypassesPermission =
        !!permissionBypassQuery &&
        new URLSearchParams(locationSearch).get(permissionBypassQuery.name) === permissionBypassQuery.value;
    const hasRequiredPermission = userHasPermissions(requiredPermission, true);
    const userIsPermitted = queryBypassesPermission || hasRequiredPermission;
    const locked = userIsLocked();

    if (locked) {
        localStorage.removeItem('isLoggedIn');
        navigate('/');
    }

    if (!user && !userLoading) {
        logIn(redirectAfterLogin);

        return null;
    }

    if (isFeatureVisible(featureName, true) && userIsPermitted) {
        return children;
    }

    navigate(ROUTES.SHOP);

    return null;
};

type FeatureRouteProps = {
    children: React.ReactElement | null;
    featureName?: string;
};

export const FeatureRoute: FC<FeatureRouteProps> = (props) => {
    const { children, featureName = '' } = props;
    const { isFeatureVisible } = useIsFeatureVisible();

    if (isFeatureVisible(featureName, true)) {
        return children;
    }

    navigate(ROUTES.SHOP);

    return null;
};
